/* Sprint UX-Polish — Web fullscreen image lightbox.
 *
 * A self-contained, controlled overlay used by the listing detail
 * gallery. Deliberately dependency-free (no new packages) — relies
 * only on lucide-react (already used app-wide) and tailwindcss-animate
 * (already used by the shadcn Sheet) for the enter transition.
 *
 * Behaviour contract:
 *   - Opens over the whole viewport (fixed inset-0, z-[100]).
 *   - Prev / Next buttons + ← / → keyboard arrows cycle images.
 *   - ESC closes, clicking the dimmed backdrop closes, X closes.
 *   - Clicking the image or a control does NOT close (stopPropagation).
 *   - Body scroll is locked while open so the page never scrolls behind.
 *   - Light touch-swipe support for touch browsers (native app has its
 *     own richer gallery; this is just a web nicety).
 *
 * It renders `null` when closed or when there are no images, so it is
 * safe to mount unconditionally.
 */
import i18n from "../i18n";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageLightbox({ urls, open, startIndex = 0, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const touchStartX = useRef(null);

  const count = Array.isArray(urls) ? urls.length : 0;

  // Seed the index whenever the lightbox is (re)opened.
  useEffect(() => {
    if (open) setCurrent(Math.min(Math.max(startIndex, 0), Math.max(count - 1, 0)));
  }, [open, startIndex, count]);

  const go = useCallback(
    (delta) => {
      setCurrent((c) => (count === 0 ? 0 : (c + delta + count) % count));
    },
    [count]
  );

  // Keyboard navigation + body-scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, go, onClose]);

  if (!open || count === 0) return null;

  const src = urls[Math.min(current, count - 1)];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bildergalerie"
      data-testid="image-lightbox"
      onClick={onClose}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchStartX.current = null;
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center
                 bg-black/90 backdrop-blur-sm p-4
                 animate-in fade-in-0 duration-150"
    >
      {/* Close */}
      <button
        type="button"
        data-testid="image-lightbox-close"
        aria-label={i18n.t("common:ui.close")}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full
                   bg-zinc-900/80 border border-zinc-700 text-zinc-200
                   hover:text-white hover:border-zinc-500 transition
                   flex items-center justify-center"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter */}
      {count > 1 && (
        <div
          data-testid="image-lightbox-counter"
          className="absolute top-5 left-1/2 -translate-x-1/2 z-10
                     text-xs text-zinc-300 bg-zinc-900/70 border border-zinc-700
                     rounded-full px-3 py-1 tabular-nums"
        >
          {current + 1} / {count}
        </div>
      )}

      {/* Previous */}
      {count > 1 && (
        <button
          type="button"
          data-testid="image-lightbox-prev"
          aria-label={i18n.t("common:ui.prevImage")}
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10
                     h-11 w-11 rounded-full bg-zinc-900/80 border border-zinc-700
                     text-zinc-200 hover:text-white hover:border-zinc-500 transition
                     flex items-center justify-center"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image (high resolution — original URL, contained in viewport) */}
      <img
        key={src}
        src={src}
        alt=""
        draggable={false}
        data-testid="image-lightbox-image"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl
                   select-none animate-in zoom-in-95 fade-in-0 duration-200"
      />

      {/* Next */}
      {count > 1 && (
        <button
          type="button"
          data-testid="image-lightbox-next"
          aria-label={i18n.t("common:ui.nextImage")}
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10
                     h-11 w-11 rounded-full bg-zinc-900/80 border border-zinc-700
                     text-zinc-200 hover:text-white hover:border-zinc-500 transition
                     flex items-center justify-center"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
